import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Translate a key via NGX TranslateService when an injection context is active.
 * Returns the original key when no service is available or the key is empty.
 */
export function safeTranslate(key: string | undefined): string | undefined {
  if (!key) return key;
  try {
    const translate = inject(TranslateService, { optional: true });
    if (translate) {
      return translate.instant(key);
    }
  } catch {
    // outside an injection context — fall through and return the original key
  }
  return key;
}

const HEADER_STRING_KEYS = ['label', 'labelTooltipIconText'] as const;
const HINT_KEYS = ['hintLeft', 'hintRight'] as const;
const PROP_STRING_KEYS = [
  'placeholder',
  'checkboxLabel',
  'text',
  'tooltip',
  'footerText',
] as const;

/**
 * Recursively translates string fields commonly used in dv-signal-form element
 * configs (labels, hints, props.placeholder/columns/items/elements).
 */
export function translateConfig<T extends Record<string, any>>(config: T): T {
  if (!config) return config;
  const result: Record<string, any> = { ...config };

  for (const key of HEADER_STRING_KEYS) {
    if (typeof result[key] === 'string') {
      result[key] = safeTranslate(result[key]);
    }
  }

  for (const hint of HINT_KEYS) {
    const value = result[hint];
    if (value && typeof value === 'object' && 'text' in value) {
      result[hint] = { ...value, text: safeTranslate(value.text) };
    }
  }

  if (result['props'] && typeof result['props'] === 'object') {
    const props = { ...result['props'] };

    for (const key of PROP_STRING_KEYS) {
      if (typeof props[key] === 'string') {
        props[key] = safeTranslate(props[key]);
      }
    }

    if (Array.isArray(props.columns)) {
      props.columns = props.columns.map((col: any) =>
        col && typeof col === 'object' && typeof col.header === 'string'
          ? { ...col, header: safeTranslate(col.header) }
          : col
      );
    }

    if (Array.isArray(props.items)) {
      props.items = props.items.map((item: any) =>
        item && typeof item === 'object' && typeof item.label === 'string'
          ? { ...item, label: safeTranslate(item.label) }
          : item
      );
    }

    if (Array.isArray(props.elements)) {
      props.elements = props.elements.map((el: any) =>
        el && typeof el === 'object' && !('ɵfac' in (el.constructor ?? {}))
          ? translateConfig(el)
          : el
      );
    }

    result['props'] = props;
  }

  return result as T;
}
